import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';

import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';

import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import {
  getSmevRequestFindResponse,
  getSmevRequestRequestTemplates,
  getSmevRequestRequestTypes,
} from '../selectors/smevRequestSelectors';
import styles from './SmevRequest.module.scss';
import {
  smevRequestCreate,
  smevRequestGetCommonData,
  smevRequestGetData,
  smevRequestSetData,
} from '../actions/SmevRequestAction';
import { ISmevMessageResponse } from '../../../APIPackage/SmevMessagesModule/interfaces/ISmevMessageResponse';
import { SmevRequestBody } from './SmevRequestBody';
import { IRequestTemplate } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestTemplate';
import { IRequestType } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestType';
import { ISmevRequestForm } from '../interfaces/ISmevRequestForm';
import { convertSmevFormAttachmentToCreateData } from '../common/convertSmevFormAttachmentToCreateData';
import { IKnmAttachment } from '../../../APIPackage/KnmModule/interfaces/IKnmAttachment';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

interface ISmevRequestProps {
  requestTemplates?: IRequestTemplate[];
  requestTypes?: IRequestType[];
  smevRequestFindResponse?: ISmevMessageResponse;
}

const SmevRequestWithoutStore = ({ requestTemplates, requestTypes, smevRequestFindResponse }: ISmevRequestProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isRequestCreations, setIsRequestCreations] = useState<boolean>(true);
  const [xmlError, setXmlError] = useState<string | undefined>();

  const formRef = useRef<FormApi<ISmevRequestForm, Partial<ISmevRequestForm>>>();

  useEffect(() => {
    dispatch(smevRequestGetCommonData());
  }, [dispatch]);

  useEffect(() => {
    if (smevRequestFindResponse && isRequestCreations) {
      setIsRequestCreations(false);
    }
  }, [isRequestCreations, smevRequestFindResponse]);

  const clearForm = useCallback(
    (options?: { keepSearchText?: boolean }) => {
      const form = formRef.current;

      if (!form) {
        return;
      }

      if (!options?.keepSearchText) {
        form.change('searchText');
      }

      form.change('adapterType');
      form.change('attachments');
      form.change('messageType');
      form.change('requestUri');
      form.change('requestTemplate');
      form.change('requestXml');
      form.change('requestTime');
      form.change('requestStatus');
      form.change('typeOfRequest');
      form.change('responseXml');
      form.change('responseTime');
      form.change('responseStatus');
      dispatch(smevRequestSetData(undefined));
      setXmlError(undefined);
    },
    [dispatch],
  );

  const handleNewRequest = useCallback(() => {
    clearForm();
    setIsRequestCreations(true);
  }, [clearForm]);

  const handleSearch = useCallback(() => {
    const searchText = formRef.current?.getState().values.searchText;

    if (!searchText) {
      return;
    }

    clearForm({ keepSearchText: true });
    setIsRequestCreations(false);
    dispatch(smevRequestGetData(searchText));
  }, [clearForm, dispatch]);

  const handleRefreshRequest = useCallback(() => {
    const searchText = formRef.current?.getState().values.searchText;
    const messageId =
      searchText ||
      smevRequestFindResponse?.requestMessage?.messageId ||
      smevRequestFindResponse?.responseMessage?.messageId;

    if (!messageId) {
      return;
    }

    if (!searchText) {
      formRef.current?.change('searchText', messageId);
    }

    const systemType = formRef.current?.getState().values.messageType?.systemType;

    dispatch(smevRequestGetData(messageId, systemType));
  }, [dispatch, smevRequestFindResponse]);

  const handleSendRequest = useCallback(() => {
    const values = formRef.current?.getState().values;

    if (!values?.requestXml || !values.requestUri || !values.typeOfRequest) {
      return;
    }

    const formAttachments = values.attachments || [];
    const hasUploadingAttachments = formAttachments.some(attachment => attachment.isNew && !attachment.guid);

    if (hasUploadingAttachments) {
      return;
    }

    const attachments = formAttachments
      .map(attachment => convertSmevFormAttachmentToCreateData(attachment))
      .filter((attachment): attachment is IKnmAttachment => Boolean(attachment));

    if (attachments.length !== formAttachments.length) {
      dispatch(
        addNoticeAction({
          renderContent: 'Не удалось подготовить вложения к отправке. Удалите повреждённые файлы и загрузите их снова.',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
        }),
      );

      return;
    }

    dispatch(
      smevRequestCreate(
        {
          content: values.requestXml,
          uri: values.requestUri,
          requestType: values.typeOfRequest,
          attachments,
        },
        values.messageType?.systemType || '',
      ),
    );
  }, [dispatch]);

  const renderForm = ({ form, values }: FormRenderProps<ISmevRequestForm>) => {
    formRef.current = form;

    return (
      <form
        onSubmit={event => {
          event.preventDefault();
          handleSearch();
        }}
      >
        <Row>
          <FieldBlock>
            <div className={styles.SmevHeader}>
              <span className={styles.SmevHeaderTitle}>Запросы СМЭВ</span>
              <TextInputField className={styles.FieldSearchString} name="searchText" placeholder="Поиск" />
              <Button
                variant="contained"
                id="searchButton"
                color="primary"
                type="button"
                text="Найти"
                disabled={!values.searchText}
                onClick={handleSearch}
              />
              {!isRequestCreations && (
                <Button
                  className="shared-btn-add"
                  text="Новый запрос"
                  type="button"
                  variant="link"
                  color="primary"
                  id="createNewRequest"
                  onClick={handleNewRequest}
                />
              )}
            </div>
          </FieldBlock>
        </Row>

        <SmevRequestBody
          isRequestCreations={isRequestCreations}
          form={form}
          requestTypes={requestTypes}
          requestTemplates={requestTemplates}
          smevRequestFindResponse={smevRequestFindResponse}
          xmlError={xmlError}
          setXmlError={setXmlError}
          onRefreshRequest={handleRefreshRequest}
          onSendRequest={handleSendRequest}
        />
      </form>
    );
  };

  return <Form<ISmevRequestForm> onSubmit={handleSearch} render={renderForm} />;
};

const mapStateToProps = (state: IRootState) => ({
  smevRequestFindResponse: getSmevRequestFindResponse(state),
  requestTemplates: getSmevRequestRequestTemplates(state),
  requestTypes: getSmevRequestRequestTypes(state),
});

export const SmevRequestForm = connect(mapStateToProps)(SmevRequestWithoutStore);
